/**
 * Store hosting the multi-source "Import connections…" review dialog. Every
 * entry point (start-page discovery card, command palette, Manage Connections
 * Import… menu, New Connection dialog, in-dialog Browse…) scans a chosen
 * source first, then drops the result
 * here to open the dialog. `sourceId` drives the dialog title/copy; `entry` is
 * the analytics entry point.
 */
import { create } from 'zustand';
import type { ImportScanResult, ImportSourceId } from '../../../types/connectionImport';

/** Where the import was launched from (analytics only). */
export type ImportEntry =
  | 'empty_state'
  | 'command_palette'
  | 'manage_dialog'
  | 'connection_dialog'
  | 'file_picker';

interface ConnectionImportState {
  isOpen: boolean;
  /**
   * Counts review opens (C3 review r2, C3-12). An import save captures it with its host when it starts; when it settles
   * it closes the review only if that is still the one it started in — never a review opened while it was held.
   */
  generation: number;
  scanResult: ImportScanResult | null;
  sourceId: ImportSourceId;
  entry: ImportEntry;
  /**
   * Azure is discovered live (no file to scan), so choosing it opens the shared
   * `AzureResourcePicker` first — `azureOpen` drives that picker; confirming a
   * pick feeds the normal review dialog via `openFromScan`.
   */
  azureOpen: boolean;
  azureEntry: ImportEntry;
  openFromScan: (
    scanResult: ImportScanResult,
    sourceId: ImportSourceId,
    entry: ImportEntry
  ) => void;
  /** Replace the current result (e.g. after an in-dialog Browse…), optionally re-sourcing entry. */
  setScanResult: (scanResult: ImportScanResult, entry?: ImportEntry) => void;
  close: () => void;
  /** Open the Azure picker for the import flow (tracks the launch entry point). */
  openAzurePicker: (entry: ImportEntry) => void;
  closeAzurePicker: () => void;
}

export const useConnectionImportStore = create<ConnectionImportState>((set) => ({
  isOpen: false,
  generation: 0,
  scanResult: null,
  sourceId: 'ads',
  entry: 'empty_state',
  azureOpen: false,
  azureEntry: 'manage_dialog',
  openFromScan: (scanResult, sourceId, entry) =>
    set((state) => ({ isOpen: true, generation: state.generation + 1, scanResult, sourceId, entry })),
  setScanResult: (scanResult, entry) =>
    set((state) => ({ scanResult, entry: entry ?? state.entry })),
  close: () => set({ isOpen: false }),
  openAzurePicker: (entry) => set({ azureOpen: true, azureEntry: entry }),
  closeAzurePicker: () => set({ azureOpen: false }),
}));
